import React, { createContext, useContext, useMemo, useState } from 'react';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import { useSuggestedTemplates } from './useSuggestedTemplates.ts';
import type { useSuggestedTemplatesQuery$data } from './__generated__/useSuggestedTemplatesQuery.graphql.ts';

type Template = NonNullable<
	NonNullable<NonNullable<useSuggestedTemplatesQuery$data['allTemplates']>['nodes']>[number]
>;

type BlankPageQuickActionsContextType = {
	templates: Template[];
	templateIndex: number;
	buttonHovered: boolean;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	setButtonHovered: (value: boolean) => void;
	setTemplateIndex: (value: number) => void;
	revampedBpqa: boolean;
};

export const BlankPageQuickActionsContext: React.Context<BlankPageQuickActionsContextType> =
	createContext<BlankPageQuickActionsContextType>({
		templates: [],
		templateIndex: 0,
		buttonHovered: false,
		loading: false,
		error: undefined,
		setButtonHovered: () => {},
		setTemplateIndex: () => {},
		revampedBpqa: false,
	});

type BlankPageQuickActionsProviderProps = {
	children: React.ReactNode;
	spaceKey: string;
};

export const BlankPageQuickActionsProvider: React.FC<BlankPageQuickActionsProviderProps> = ({
	children,
	spaceKey,
}) => {
	const [templateIndex, setTemplateIndex] = useState<number>(0);
	const [buttonHovered, setButtonHovered] = useState<boolean>(false);

	const { error, loading, templates, isNewUserTemplates } = useSuggestedTemplates({
		spaceKey,
	});

	const contextValue = useMemo(() => {
		return {
			templates,
			error,
			loading,
			templateIndex,
			setTemplateIndex,
			buttonHovered,
			setButtonHovered,
			revampedBpqa: Boolean(isNewUserTemplates),
		};
	}, [
		templates,
		error,
		loading,
		templateIndex,
		setTemplateIndex,
		buttonHovered,
		setButtonHovered,
		isNewUserTemplates,
	]);

	return (
		<BlankPageQuickActionsContext.Provider value={contextValue}>
			{children}
		</BlankPageQuickActionsContext.Provider>
	);
};

export const useBlankPageQuickActionsContext = (): BlankPageQuickActionsContextType =>
	useContext(BlankPageQuickActionsContext);
